import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, Clock, AlertCircle, CheckCircle, MessageCircle } from 'lucide-react';

const DashboardStats = ({ stats, onCardClick, selectedPriority, activeTab, products }) => {
  if (!stats) return null;

  // Calculate tab-specific stats for product tabs
  const calculateTabStats = () => {
    if (!products || !['product_development', 'product_marketing', 'sales'].includes(activeTab)) {
      return null;
    }

    const tabStats = {
      total: products.length,
      high: products.filter(p => p.priority === 'high').length,
      medium: products.filter(p => p.priority === 'medium').length,
      low: products.filter(p => p.priority === 'low').length
    };

    return tabStats;
  };

  const tabStats = calculateTabStats();

  const priorityStats = [
    {
      label: 'High Priority',
      value: tabStats ? tabStats.high : stats.priority_distribution.high,
      color: 'from-red-500 to-red-600',
      selectedColor: 'from-red-400 to-red-500',
      icon: AlertCircle,
      filter: 'high'
    },
    {
      label: 'Medium Priority',
      value: tabStats ? tabStats.medium : stats.priority_distribution.medium,
      color: 'from-yellow-500 to-yellow-600',
      selectedColor: 'from-yellow-400 to-yellow-500',
      icon: Clock,
      filter: 'medium'
    },
    {
      label: 'Low Priority',
      value: tabStats ? tabStats.low : stats.priority_distribution.low,
      color: 'from-green-500 to-green-600',
      selectedColor: 'from-green-400 to-green-500',
      icon: CheckCircle,
      filter: 'low'
    }
  ];

  // Special stats for non-product tabs
  const renderSpecialTabStats = () => {
    switch (activeTab) {
      case 'messages':
        return (
          <>
            {/* Unread Messages */}
            <Card className="card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 bg-gradient-to-br from-white to-slate-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Unread Messages
                </CardTitle>
                <div className="p-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-teal-500 to-cyan-600">
                  <MessageCircle className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.unread_messages || 0}
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Need attention
                </p>
              </CardContent>
            </Card>

            {/* Total Conversations */}
            <Card className="card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 bg-gradient-to-br from-white to-slate-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Total Conversations
                </CardTitle>
                <div className="p-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-blue-500 to-indigo-600">
                  <TrendingUp className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.total_conversations || 0}
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Active conversations
                </p>
              </CardContent>
            </Card>

            {/* Active Users */}
            <Card className="card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 bg-gradient-to-br from-white to-slate-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Active Users
                </CardTitle>
                <div className="p-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-green-500 to-emerald-600">
                  <CheckCircle className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.active_users || 0}
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Currently online
                </p>
              </CardContent>
            </Card>
          </>
        );

      case 'admin':
        return (
          <>
            {/* Total Users */}
            <Card className="card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 bg-gradient-to-br from-white to-slate-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Total Users
                </CardTitle>
                <div className="p-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-blue-500 to-indigo-600">
                  <TrendingUp className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.total_users || 0}
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Registered users
                </p>
              </CardContent>
            </Card>

            {/* Active Users */}
            <Card className="card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 bg-gradient-to-br from-white to-slate-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Active Users
                </CardTitle>
                <div className="p-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-green-500 to-emerald-600">
                  <CheckCircle className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.active_users || 0}
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Currently active
                </p>
              </CardContent>
            </Card>

            {/* Pending Users */}
            <Card className="card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 bg-gradient-to-br from-white to-slate-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Pending Users
                </CardTitle>
                <div className="p-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-yellow-500 to-orange-600">
                  <Clock className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.pending_users || 0}
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Awaiting approval
                </p>
              </CardContent>
            </Card>
          </>
        );

      case 'activity':
        return (
          <>
            {/* Recent Activities */}
            <Card className="card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 bg-gradient-to-br from-white to-slate-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Recent Activities
                </CardTitle>
                <div className="p-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-green-500 to-emerald-600">
                  <TrendingUp className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.recent_activities || 0}
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Last 24 hours
                </p>
              </CardContent>
            </Card>

            {/* System Events */}
            <Card className="card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 bg-gradient-to-br from-white to-slate-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  System Events
                </CardTitle>
                <div className="p-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-orange-500 to-red-600">
                  <AlertCircle className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.system_events || 0}
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Need attention
                </p>
              </CardContent>
            </Card>

            {/* Active Sessions */}
            <Card className="card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 bg-gradient-to-br from-white to-slate-50">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-slate-600">
                  Active Sessions
                </CardTitle>
                <div className="p-2 rounded-lg transition-all duration-200 bg-gradient-to-r from-blue-500 to-indigo-600">
                  <CheckCircle className="h-4 w-4 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-slate-900">
                  {stats.active_sessions || 0}
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  Currently logged in
                </p>
              </CardContent>
            </Card>
          </>
        );

      default:
        return null;
    }
  };

  const shouldShowProductStats = ['product_development', 'product_marketing', 'sales'].includes(activeTab);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in">
      {/* Total Products (only for product tabs) */}
      {shouldShowProductStats && (
        <Card 
          className={`card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 ${
            selectedPriority === 'all' 
              ? 'bg-gradient-to-br from-blue-100 to-blue-200 border-2 border-blue-300 shadow-xl' 
              : 'bg-gradient-to-br from-white to-slate-50'
          }`}
          onClick={() => onCardClick && onCardClick('all')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">
              Total Products
            </CardTitle>
            <div className={`p-2 rounded-lg transition-all duration-200 ${
              selectedPriority === 'all' 
                ? 'bg-gradient-to-r from-blue-500 to-indigo-600 shadow-lg transform scale-110' 
                : 'bg-gradient-to-r from-blue-500 to-indigo-600'
            }`}>
              <TrendingUp className="h-4 w-4 text-white" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-900">
              {tabStats ? tabStats.total : stats.total_products}
            </div>
            <p className="text-xs text-slate-600 mt-1">
              {tabStats ? `in ${activeTab.replace('_', ' ')}` : 'Across all departments'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Priority Stats (only for product tabs) */}
      {shouldShowProductStats && priorityStats.map((stat, index) => {
        const Icon = stat.icon;
        const isSelected = selectedPriority === stat.filter;
        return (
          <Card 
            key={stat.label} 
            className={`card-hover border-0 shadow-lg cursor-pointer transition-all duration-200 hover:scale-105 ${
              isSelected 
                ? `bg-gradient-to-br ${
                    stat.filter === 'high' ? 'from-red-100 to-red-200 border-2 border-red-300' :
                    stat.filter === 'medium' ? 'from-yellow-100 to-yellow-200 border-2 border-yellow-300' :
                    'from-green-100 to-green-200 border-2 border-green-300'
                  } shadow-xl` 
                : 'bg-gradient-to-br from-white to-slate-50'
            }`}
            onClick={() => onCardClick && onCardClick(stat.filter)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">
                {stat.label}
              </CardTitle>
              <div className={`p-2 rounded-lg transition-all duration-200 ${
                isSelected 
                  ? `bg-gradient-to-r ${stat.color} shadow-lg transform scale-110` 
                  : `bg-gradient-to-r ${stat.color}`
              }`}>
                <Icon className="h-4 w-4 text-white" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-slate-900">
                {stat.value}
              </div>
              <p className="text-xs text-slate-600 mt-1">
                {stat.value === 1 ? 'product' : 'products'}
              </p>
            </CardContent>
          </Card>
        );
      })}

      {/* Special stats for non-product tabs */}
      {!shouldShowProductStats && renderSpecialTabStats()}
    </div>
  );
};

export default DashboardStats;